import _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { IReactTranslateService } from '../../../common/react/IReactTranslateService';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { SiteRolesResource } from './SiteRoles';

export type LicensingTier = 'Creator' | 'Viewer' | 'Explorer' | 'Unlicensed';
export const CreatorTier: LicensingTier = 'Creator';
export const ViewerTier: LicensingTier = 'Viewer';
export const ExplorerTier: LicensingTier = 'Explorer';
export const UnlicensedTier: LicensingTier = 'Unlicensed';
export const LicensingTierLocPrefix = 'license_';
export const DefaultSiteRole = SiteRoles.ExplorerCanPublish;

export interface SiteRoleOption {
  role: string;
  licenseTierRole: LicensingTier;
  locKey: string;
  name: () => string;
  disabled?: boolean;
}

const SiteAdministratorCreator: SiteRoleOption = {
  role: SiteRoles.SiteAdministratorCreator,
  licenseTierRole: CreatorTier,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.SiteAdministratorCreator,
  name: Messages.siteRole_SiteAdministratorCreator
};
const Creator: SiteRoleOption = {
  role: SiteRoles.Creator,
  licenseTierRole: CreatorTier,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.Creator,
  name: Messages.siteRole_Creator
};
const Viewer: SiteRoleOption = {
  role: SiteRoles.Viewer,
  licenseTierRole: ViewerTier,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.Viewer,
  name: Messages.siteRole_Viewer
};
const Unlicensed: SiteRoleOption = {
  role: SiteRoles.Unlicensed,
  licenseTierRole: UnlicensedTier,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.Unlicensed,
  name: Messages.siteRole_Unlicensed
};
const Mixed: SiteRoleOption = { // this is just to support the "Mixed" role in Server Users Site Membership dialog, and will not be shown in the dropdown
  role: SiteRolesResource.Mixed,
  licenseTierRole: undefined,
  locKey: SiteRolesResource.LocPrefix + SiteRolesResource.Mixed,
  name: Messages.siteRole_Mixed
};
const SupportUser: SiteRoleOption = { // this is just to support the "Support User" role in Site Users page for online usecase and will not be shown in the dropdown
  role: SiteRoles.SupportUser,
  licenseTierRole: undefined,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.SupportUser,
  name: Messages.siteRole_SupportUser
};
const Explorer: SiteRoleOption = {
  role: SiteRoles.Explorer,
  licenseTierRole: ExplorerTier,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.Explorer,
  name: Messages.siteRole_Explorer
};
const ExplorerCanPublish: SiteRoleOption = {
  role: SiteRoles.ExplorerCanPublish,
  licenseTierRole: ExplorerTier,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.ExplorerCanPublish,
  name: Messages.siteRole_ExplorerCanPublish
};
const SiteAdministratorExplorer: SiteRoleOption = {
  role: SiteRoles.SiteAdministratorExplorer,
  licenseTierRole: ExplorerTier,
  locKey: SiteRolesResource.LocPrefix + SiteRoles.SiteAdministratorExplorer,
  name: Messages.siteRole_SiteAdministratorExplorer
};
const CreatorTierSiteRoles: SiteRoleOption[] = [
  SiteAdministratorCreator,
  Creator
];
const AnySiteRole: SiteRoleOption = { // from SiteRoleFilterHelper, enables SiteRolePickerMenu to optionally
  role: SiteRolesResource.AnyRole, // include an "any" option
  licenseTierRole: undefined,
  locKey: SiteRolesResource.LocPrefix + SiteRolesResource.AnyRole,
  name: Messages.siteRole_Any
};

export class SiteRoleOptionsResource{

  public static readonly instance = new SiteRoleOptionsResource();

  private allSiteRoleOptions(): SiteRoleOption[] {
    let siteRoleOptions: SiteRoleOption[] = [];
    siteRoleOptions = siteRoleOptions.concat(
      this.getServerAdministratorSiteRoleOption(),
      this.getCreatorTierRoles(),
      SiteAdministratorExplorer,
      ExplorerCanPublish,
      Explorer,
      Viewer,
      Unlicensed);
    return siteRoleOptions;
  }

  private getCreatorTierRoles(): SiteRoleOption[] {
    const sessionManagementService = getSessionManagementServiceInstance();
    const hasCreatorTier = sessionManagementService.hasCreatorTier();
    return CreatorTierSiteRoles.map((siteRoleOption: SiteRoleOption) => {
      siteRoleOption.disabled = !hasCreatorTier;
      return siteRoleOption;
    });
  }

  private getServerAdministratorSiteRoleOption(): SiteRoleOption {
    const sessionManagementService = getSessionManagementServiceInstance();
    return {
        role: SiteRoles.ServerAdministrator,
        licenseTierRole: sessionManagementService.hasCreatorTier() ? CreatorTier : ExplorerTier,
        locKey: SiteRolesResource.LocPrefix + SiteRoles.ServerAdministrator,
        name: Messages.siteRole_ServerAdministrator
    };
  }

  public getAnySiteRole(): SiteRoleOption {
    return AnySiteRole;
  }

  public getAllSiteRoleOptions(): SiteRoleOption[] {
    return this.getAllSiteRoleOptionsExcept();
  }

  public getAllSiteRoleOptionsExcept(...rolesToExclude: string[]): SiteRoleOption[] {
    return _.reject(this.allSiteRoleOptions(), (roleOption: SiteRoleOption) => {
      return _.contains(rolesToExclude, roleOption.role);
    });
  }

  public getSiteRoleOptionBySiteRole(siteRole: string): SiteRoleOption {
    const allSiteRoleOptions: SiteRoleOption[] = this.getAllSiteRoleOptions();
    for (const siteRoleOption of allSiteRoleOptions) {
      if (siteRoleOption.role === siteRole) {
        return siteRoleOption;
      }
    }

    if (siteRole === SiteRolesResource.AnyRole) {
      return this.getAnySiteRole();
    }

    if (Mixed.role === siteRole) {
      return Mixed;
    }

    return undefined;
  }

  public getSiteRoleLockey(siteRole: string): string {
    if (siteRole === SiteRoles.SupportUser) {
      return SupportUser.locKey;
    }
    const siteRoleOption = this.getSiteRoleOptionBySiteRole(siteRole);
    if (siteRoleOption) {
      return siteRoleOption.locKey;
    }
    return undefined;
  }

  public getSiteRoleName(siteRole: string): () => string {
    if (siteRole === SiteRoles.SupportUser) {
      return SupportUser.name;
    }
    const siteRoleOption = this.getSiteRoleOptionBySiteRole(siteRole);
    if (siteRoleOption) {
      return siteRoleOption.name;
    }

    return () => '';
  }

  public getLicenseTierKey(licenseTier: LicensingTier): string {
    if (licenseTier) {
      return LicensingTierLocPrefix + licenseTier;
    }
    return undefined;
  }

  public siteRoleTransform(translate: IReactTranslateService): (siteRole: string) => string {
    return (siteRole: string) => siteRole ? translate(this.getSiteRoleLockey(siteRole)) : '';
  }

}

export default  SiteRoleOptionsResource.instance;